import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { ALL_DOMAINS, DOMAIN_CONFIG } from "@/lib/domainConfig";
import { absoluteUrl } from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import {
  Copy,
  ExternalLink,
  Download,
  RefreshCw,
  Upload,
  RotateCcw,
  Info,
} from "lucide-react";

const LOCALES = [
  { code: "de", label: "DE" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

const ogUrl = (host, locale, cacheBuster) =>
  `${absoluteUrl("/api/og.png")}?domain=${encodeURIComponent(host)}&locale=${locale}${
    cacheBuster ? `&v=${cacheBuster}` : ""
  }`;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AdminOgPreview = () => {
  const [activeLocale, setActiveLocale] = useState("de");
  const [refreshKey, setRefreshKey] = useState(0);
  const [overrides, setOverrides] = useState({}); // { "host|locale": {size_bytes, updated_at} }
  const [uploadingKey, setUploadingKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const fileInputs = useRef({});

  const cards = useMemo(
    () =>
      ALL_DOMAINS.map((host) => ({
        host,
        cfg: DOMAIN_CONFIG[host],
      })),
    [],
  );

  const loadOverrides = async () => {
    try {
      const { data } = await api.get("/admin/og/overrides");
      const map = {};
      (data || []).forEach((o) => {
        map[`${o.domain}|${o.locale}`] = o;
      });
      setOverrides(map);
    } catch (err) {
      // Silent — not critical
    }
  };

  useEffect(() => {
    loadOverrides();
  }, []);

  const refresh = () => {
    setRefreshKey(Date.now());
    toast.success("Vorschau-Karten neu geladen");
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("URL kopiert");
    } catch {
      toast.error("Konnte URL nicht kopieren");
    }
  };

  const downloadAll = () => {
    cards.forEach((c, i) => {
      const a = document.createElement("a");
      a.href = ogUrl(c.host, activeLocale, refreshKey);
      a.download = `og_${c.host.replace(/\./g, "_")}_${activeLocale}.png`;
      a.style.display = "none";
      document.body.appendChild(a);
      setTimeout(() => {
        a.click();
        document.body.removeChild(a);
      }, i * 200);
    });
    toast.success(`${cards.length} Karten werden heruntergeladen…`);
  };

  const uploadFile = async (host, locale, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte ein Bild auswählen (PNG oder JPG).");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Bild zu groß (max. 4 MB).");
      return;
    }
    const key = `${host}|${locale}`;
    setUploadingKey(key);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post("/admin/og/override", {
        domain: host,
        locale,
        image_data_url: dataUrl,
      });
      setOverrides((prev) => ({
        ...prev,
        [key]: {
          domain: host,
          locale,
          size_bytes: data.size_bytes,
          updated_at: new Date().toISOString(),
        },
      }));
      setRefreshKey(Date.now());
      toast.success(`Eigene Karte für ${host} (${locale.toUpperCase()}) aktiv.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingKey(null);
    }
  };

  const clearOverride = async (host, locale) => {
    const key = `${host}|${locale}`;
    try {
      await api.delete("/admin/og/override", {
        params: { domain: host, locale },
      });
      setOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setRefreshKey(Date.now());
      toast.success(`${host} (${locale.toUpperCase()}) auf Standard zurückgesetzt.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AdminShell
      title="Social Cards"
      subtitle={`Open-Graph Vorschau für alle ${cards.length} Domains × ${LOCALES.length} Sprachen`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={refresh}
            data-testid="og-refresh-btn"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
          >
            <RefreshCw className="w-4 h-4" />
            Neu laden
          </button>
          <button
            onClick={downloadAll}
            data-testid="og-download-all-btn"
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
          >
            <Download className="w-4 h-4" />
            Alle herunterladen ({activeLocale.toUpperCase()})
          </button>
        </div>
      }
    >
      {/* Image size hint banner */}
      <div className="mb-6 rounded-2xl border border-sky-100 bg-sky-50/70 px-5 py-4 flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-sky-100 text-sky-700 grid place-items-center">
          <Info className="w-4 h-4" />
        </div>
        <div className="text-sm text-neutral-700 leading-relaxed">
          <div className="font-medium text-neutral-950">Bildmaße für eigene Social Cards</div>
          <div className="mt-0.5">
            <span className="font-medium">1200 × 630 px</span> (Seitenverhältnis 1,91:1) ·
            PNG oder JPG · max. 4&nbsp;MB · idealerweise unter 500&nbsp;KB für schnellere
            Vorschau. Texte/Logo bitte mind. 200&nbsp;px vom Rand wegrücken, damit
            WhatsApp/Facebook nichts beschneidet.
          </div>
        </div>
      </div>

      {/* Locale switcher */}
      <div className="mb-6 inline-flex items-center gap-1 rounded-full bg-neutral-100 p-1">
        {LOCALES.map((l) => {
          const active = l.code === activeLocale;
          return (
            <button
              key={l.code}
              onClick={() => setActiveLocale(l.code)}
              data-testid={`og-locale-${l.code}`}
              data-active={active}
              className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all ${
                active ? "bg-neutral-950 text-white shadow-sm" : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-neutral-500 mb-6">
        So sehen die Vorschau-Karten aus, wenn jemand deine Domains bei WhatsApp,
        Facebook, LinkedIn oder Twitter teilt. Karte per Drag-and-Drop ersetzen
        oder Klick auf „Eigenes Bild hochladen". Mit „Zurücksetzen" wieder die
        automatisch generierte Standard-Karte verwenden.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {cards.map(({ host, cfg }) => {
          const url = ogUrl(host, activeLocale, refreshKey);
          const overrideKey = `${host}|${activeLocale}`;
          const hasOverride = !!overrides[overrideKey];
          const isUploading = uploadingKey === overrideKey;
          const isDragging = dragOverKey === overrideKey;
          const safeId = host.replace(/\./g, "-");

          const onDrop = async (e) => {
            e.preventDefault();
            setDragOverKey(null);
            const file = e.dataTransfer?.files?.[0];
            if (file) await uploadFile(host, activeLocale, file);
          };

          return (
            <div
              key={host}
              data-testid={`og-card-${safeId}`}
              className={`group rounded-3xl bg-white border overflow-hidden transition-all ${
                isDragging
                  ? "border-sky-500 shadow-[0_18px_40px_rgba(2,132,199,0.18)] ring-2 ring-sky-200"
                  : "border-neutral-200 hover:shadow-[0_18px_40px_rgba(0,0,0,0.06)]"
              }`}
            >
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverKey(overrideKey);
                }}
                onDragLeave={() => setDragOverKey(null)}
                onDrop={onDrop}
                className="relative aspect-[1200/630] bg-neutral-100"
              >
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full h-full"
                >
                  <img
                    src={url}
                    alt={`Open Graph card for ${host}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </a>
                {hasOverride && (
                  <div className="absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-500/95 text-white font-medium">
                    Eigenes Bild
                  </div>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-black/70 text-white text-[10px] px-2 py-1 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  Öffnen
                </div>
                {/* Drag overlay */}
                {isDragging && (
                  <div className="absolute inset-0 grid place-items-center bg-sky-500/15 backdrop-blur-[2px]">
                    <div className="rounded-2xl bg-white/95 px-5 py-3 text-sm font-medium text-sky-700 shadow-lg">
                      Bild hier ablegen
                    </div>
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 grid place-items-center bg-white/80">
                    <div className="text-sm text-neutral-700">Lade hoch…</div>
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-sm font-medium text-neutral-950 truncate">
                      {cfg.brandName}
                    </div>
                    <div className="text-xs text-neutral-500 truncate" title={host}>
                      {host}
                    </div>
                  </div>
                  {cfg.isMain && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700">
                      Hauptmarke
                    </span>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    ref={(el) => {
                      if (el) fileInputs.current[overrideKey] = el;
                    }}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    data-testid={`og-file-input-${safeId}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadFile(host, activeLocale, file);
                      e.target.value = "";
                    }}
                  />
                  <button
                    onClick={() => fileInputs.current[overrideKey]?.click()}
                    data-testid={`og-upload-${safeId}`}
                    disabled={isUploading}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full bg-neutral-950 text-white px-3 py-1.5 hover:bg-black disabled:opacity-50"
                  >
                    <Upload className="w-3 h-3" />
                    {hasOverride ? "Ersetzen" : "Eigenes Bild"}
                  </button>
                  {hasOverride && (
                    <button
                      onClick={() => clearOverride(host, activeLocale)}
                      data-testid={`og-clear-${safeId}`}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-neutral-200 bg-white px-3 py-1.5 hover:bg-neutral-50"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Zurücksetzen
                    </button>
                  )}
                  <button
                    onClick={() => copy(url)}
                    data-testid={`og-copy-${safeId}`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full border border-neutral-200 bg-white px-3 py-1.5 hover:bg-neutral-50"
                  >
                    <Copy className="w-3 h-3" />
                    URL
                  </button>
                  <a
                    href={url}
                    download={`og_${host.replace(/\./g, "_")}_${activeLocale}.png`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-full border border-neutral-200 bg-white px-3 py-1.5 hover:bg-neutral-50"
                    data-testid={`og-download-${safeId}`}
                  >
                    <Download className="w-3 h-3" />
                    PNG
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 rounded-3xl bg-sky-50 border border-sky-100 p-6 text-sm text-neutral-700">
        <div className="font-medium text-neutral-950 mb-2">💡 So testest du das Sharing:</div>
        <ul className="space-y-1.5 list-disc pl-5">
          <li>
            <a
              href="https://www.opengraph.xyz/"
              target="_blank"
              rel="noreferrer"
              className="text-sky-700 hover:underline"
            >
              opengraph.xyz
            </a>{" "}
            – zeigt dir die Vorschau für jedes soziale Netzwerk in einer Übersicht.
          </li>
          <li>
            <a
              href="https://developers.facebook.com/tools/debug/"
              target="_blank"
              rel="noreferrer"
              className="text-sky-700 hover:underline"
            >
              Facebook Sharing Debugger
            </a>{" "}
            – nach dem Domain-Setup hier die Cache leeren, damit FB/WhatsApp die neuen Karten ziehen.
          </li>
          <li>
            <a
              href="https://www.linkedin.com/post-inspector/"
              target="_blank"
              rel="noreferrer"
              className="text-sky-700 hover:underline"
            >
              LinkedIn Post Inspector
            </a>{" "}
            – das gleiche für LinkedIn.
          </li>
        </ul>
      </div>
    </AdminShell>
  );
};

export default AdminOgPreview;
